// Third Party
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps, generatePath } from 'react-router';

// Components
import { Header, OverlappingBackground } from '../../../components/layout';
import { PrimaryButton } from '../../../components/buttons';
import ComparisonsFilterNavigation, { COMPARISONS_FILTER } from './ComparisonsFilterNavigation';
import ComparisonsList from './ComparisonsList';
import ComparisonsSection from './ComparisonsSection';
import { DeleteComparisonsModal, ComparisonMissingItemsModal } from '../../../components/modals';
import FactSetLanding from '../../fact-set/Landing';

// Utils
import heapUtils, { HEAP_CUSTOM_EVENTS } from '../../../../utils/3rd-party/heap';
import { FormattedMessage, formatMessage } from '../../../../utils/i18n';
import { NAV_PATHS, COMPARISON_NAV_PATHS } from '../../../../utils/constants';
import { userHasPermission, PERMISSIONS } from '../../../../utils/permissions';
import { getContactLayout, CONTACT_LAYOUT } from '../../../../utils/contacts';
import { comparisonHasMissingItems } from '../../../../utils/comparison/index';

// Actions
import { ReduxState } from '../../../../redux/modules/types';
import { ComparisonsListState } from '../../../../redux/modules/types/comparison';
import {
  getRecentComparisons,
  getAssignedComparisons,
  getDraftComparisons,
  getComplianceComparisons,
  deleteComparison,
  getComparisonById,
  loadComparison,
  clearComparisonsCache,
  skipEmptyState,
  updateComparison,
  removeMissingComparisonData,
  rerunComparison,
  createComparison,
  closeComparison,
  DELETE_COMPARISON,
} from '../../../../redux/modules/comparison';
import { GET_POST_MESSAGE_CONTACTS, sendPostMessageGetContactsAndPortfolios, getContactById, loadContactData } from '@/redux/modules/contacts';

import { resetGif } from '../../../../redux/modules/common/actionCreators';
import { selectIsLoadingParamsEvery, startLoaderAction, stopLoaderAction, selectIsLoadingEvery } from '../../../../redux/modules/ui/loaders';

// Types
import ThemeMode from '@/types/theme-mode';

// Utils
import { comparisonHasGifItems } from '../../../../utils/comparison';
import notification from '../../../../utils/notification';
import { ComparisonsTableFields } from '../../../components/tables/ComparisonsTable';
import Dictionary from '@/types/dictionary';
import Locale from '@/types/locale';
import Comparison from '@/types/comparison';
import GenericContact from '@/types/generic-contact';
import ReducedComparison from '@/types/reduced-comparison';
import ComparisonContact from '@/types/comparison-contact';
import User from '@/types/user';
import AppMode from '@/types/app-mode';

type StateProps = {
  comparisons: {
    recent: ComparisonsListState;
    assigned: ComparisonsListState;
    drafts: ComparisonsListState;
    compliance: ComparisonsListState;
  };
  isPostMessageActive: boolean;
  locale: Locale;
  loadedComparison?: Comparison;
  isDeletingComparison: boolean;
  openingComparisonIds: string[];
};

type DispatchProps = {
  createComparison: (contact?: GenericContact) => Promise<Comparison>;
  getContactById: (id: string) => Promise<GenericContact>;
  loadContactData: (contact: GenericContact) => Promise<void>;
  getRecentComparisons: (query?: string, nextToken?: string) => Promise<ReducedComparison[]>;
  getDraftComparisons: (query?: string, nextToken?: string) => Promise<ReducedComparison[]>;
  getComplianceComparisons: (query?: string, nextToken?: string) => Promise<ReducedComparison[]>;
  getAssignedComparisons: (query?: string, nextToken?: string) => Promise<ReducedComparison[]>;
  loadComparison: (comparison: Comparison) => Promise<Comparison>;
  getComparisonById: (id: string, contact?: ComparisonContact) => Promise<Comparison>;
  deleteComparison: (comparison: ReducedComparison) => Promise<void>;
  clearComparisonsCache: () => void;
  skipEmptyState: () => void;
  resetGif: () => void;
  updateComparison: (id: string, body: Partial<Comparison>, isNewlyAssigned?: boolean) => Promise<Comparison>;
  removeMissingComparisonData: (comparison: Comparison) => Promise<Comparison>;
  rerunComparison: (comparison: Comparison) => Promise<Comparison>;
  closeComparison: (comparison: Comparison) => void;
  startLoaderAction: (name: string, params?: Dictionary<unknown>) => void;
  stopLoaderAction: (name: string) => void;
  sendPostMessageGetContactsAndPortfolios: () => void;
};

type Props = RouteComponentProps &
  StateProps &
  DispatchProps & {
    user: User;
  };

type State = {
  selectedFilter: COMPARISONS_FILTER;
  showDeleteComparisonsModal: boolean;
  showComparisonMissingItemsModal: boolean;
  selectedComparisonsForDelete: ReducedComparison[];
  isLoadingRecentComparisons: boolean;
};

class Comparisons extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      selectedFilter: COMPARISONS_FILTER.RECENT,
      showDeleteComparisonsModal: false,
      showComparisonMissingItemsModal: this.props.loadedComparison != null && comparisonHasMissingItems(this.props.loadedComparison),
      selectedComparisonsForDelete: [],
      isLoadingRecentComparisons: false,
    };
  }

  componentDidMount() {
    const { isPostMessageActive } = this.props;
    if (isPostMessageActive) {
      this.props.startLoaderAction(GET_POST_MESSAGE_CONTACTS.type);
      this.props.sendPostMessageGetContactsAndPortfolios();
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { loadedComparison } = this.props;
    if (!prevProps.loadedComparison && loadedComparison && comparisonHasMissingItems(loadedComparison)) {
      this.setState({ showComparisonMissingItemsModal: true });
    }
  }

  componentWillUnmount() {
    this.props.clearComparisonsCache();
  }

  onFilterChange = (value: COMPARISONS_FILTER) => {
    const { selectedFilter } = this.state;

    if (value !== selectedFilter) {
      this.setState({ selectedFilter: value });
    }
  };

  renderGifButton = () => {
    const imgUrl = `https://capintel-uploads.s3.ca-central-1.amazonaws.com/enterprise-logos/2024/bmo-insurance-gif-white_${this.props.locale}.svg`;
    return (
      <PrimaryButton className="start-bmo-gif" onClick={this.createBMOGIFComparison}>
        <img alt="" src={imgUrl} />
      </PrimaryButton>
    );
  };

  onCreateComparison = () => {
    const { loadedComparison } = this.props;
    if (loadedComparison) {
      this.props.closeComparison(loadedComparison);
    }
    this.props.history.push(NAV_PATHS.COMPARISON);
  };

  loadComparisons = (query?: string, useNextToken?: boolean) => {
    const { selectedFilter } = this.state;

    let nextToken: string | undefined;

    switch (selectedFilter) {
      case COMPARISONS_FILTER.RECENT: {
        this.setState({ isLoadingRecentComparisons: true });

        if (useNextToken) {
          ({ nextToken } = this.props.comparisons.recent);
        }

        return this.props.getRecentComparisons(query, nextToken).finally(() => {
          this.setState({ isLoadingRecentComparisons: false });
        });
      }
      case COMPARISONS_FILTER.DRAFTS: {
        if (useNextToken) {
          ({ nextToken } = this.props.comparisons.drafts);
        }

        return this.props.getDraftComparisons(query, nextToken);
      }
      case COMPARISONS_FILTER.ASSIGNED: {
        if (useNextToken) {
          ({ nextToken } = this.props.comparisons.assigned);
        }

        return this.props.getAssignedComparisons(query, nextToken);
      }
      case COMPARISONS_FILTER.COMPLIANCE: {
        if (useNextToken) {
          ({ nextToken } = this.props.comparisons.compliance);
        }

        return this.props.getComplianceComparisons(query, nextToken);
      }
      default: {
        return Promise.reject(new Error('Unsupported comparisons filter.'));
      }
    }
  };

  onOpenComparison = (comparison: ReducedComparison) => {
    if (!comparison._id) {
      return;
    }

    this.props.startLoaderAction('OPEN_COMPARISON', { comparisonId: comparison._id });

    this.props.getComparisonById(comparison._id, comparison.contact).then((dbComparison: Comparison) => {
      this.props.loadComparison(dbComparison).then(result => {
        if (comparisonHasMissingItems(result)) {
          this.setState({ showComparisonMissingItemsModal: true });
          this.props.stopLoaderAction('OPEN_COMPARISON');
          return;
        }

        heapUtils.track(HEAP_CUSTOM_EVENTS.LOAD_EXISTING_COMPARISON);
        if (comparison.contact && comparison.contact._id) {
          this.props.getContactById(comparison.contact._id).then(contact => {
            this.props.loadContactData(contact);
          });
        }

        this.props.stopLoaderAction('OPEN_COMPARISON');
        const pathname = generatePath(NAV_PATHS.COMPARISON_DETAILS, {
          comparisonId: comparison._id || '',
        });
        const contactSearch = comparison.contact?._id ? `?contact=${comparison.contact?._id}` : '';

        if (comparisonHasGifItems(dbComparison)) {
          this.props.skipEmptyState();
          this.props.resetGif();
          this.props.history.push({
            pathname: `${pathname}${COMPARISON_NAV_PATHS.GIF}`,
            search: contactSearch,
          });
        } else {
          this.props.history.push({ pathname, search: contactSearch });
        }
      });
    });
  };

  onDeleteComparison = (comparison: ReducedComparison) => {
    this.setState({
      showDeleteComparisonsModal: true,
      selectedComparisonsForDelete: [comparison],
    });
  };

  onSaveComparison = (id: string, name: string, contact?: GenericContact, isNewlyAssigned?: boolean) =>
    this.props.updateComparison(id, { name, contact }, isNewlyAssigned).then(comparison => {
      notification.success(formatMessage('Comparison.SaveSuccess'));
      return comparison;
    });

  getComparisonsList = () => {
    const { comparisons, user, openingComparisonIds } = this.props;
    const { selectedFilter } = this.state;

    if (!user) {
      return null;
    }

    const classes = 'comparisons-list flex-fill';
    const layout = getContactLayout(user);

    const savedToContactLabel =
      layout === CONTACT_LAYOUT.CLIENT ? formatMessage('ComparisonHome.Filters.SavedToClient') : formatMessage('ComparisonHome.Filters.SavedToContact');

    const searchContactPlaceholder =
      layout === CONTACT_LAYOUT.CLIENT ? formatMessage('ComparisonHome.SearchClientPlaceholder') : formatMessage('ComparisonHome.SearchContactPlaceholder');

    switch (selectedFilter) {
      case COMPARISONS_FILTER.RECENT: {
        return (
          <ComparisonsSection
            user={user}
            key={COMPARISONS_FILTER.RECENT}
            tableFields={[
              ComparisonsTableFields.Name,
              ComparisonsTableFields.SavedTo,
              ComparisonsTableFields.LastModified,
              ComparisonsTableFields.Open,
              ComparisonsTableFields.Actions,
            ]}
            title={formatMessage('ComparisonHome.Filters.Recent')}
            className={classes}
            comparisons={comparisons.recent.list}
            hasMore={!!comparisons.recent.nextToken}
            loadComparisons={this.loadComparisons}
            onOpenComparison={this.onOpenComparison}
            onSaveComparison={this.onSaveComparison}
            onDeleteComparison={this.onDeleteComparison}
            searchPlaceholder={searchContactPlaceholder}
            openingComparisonIds={openingComparisonIds}
          />
        );
      }
      case COMPARISONS_FILTER.ASSIGNED: {
        return (
          <ComparisonsSection
            user={user}
            key={COMPARISONS_FILTER.ASSIGNED}
            tableFields={[
              ComparisonsTableFields.Name,
              ComparisonsTableFields.SavedTo,
              ComparisonsTableFields.LastModified,
              ComparisonsTableFields.Open,
              ComparisonsTableFields.Actions,
            ]}
            title={savedToContactLabel}
            className={classes}
            comparisons={comparisons.assigned.list}
            hasMore={!!comparisons.assigned.nextToken}
            loadComparisons={this.loadComparisons}
            onOpenComparison={this.onOpenComparison}
            onSaveComparison={this.onSaveComparison}
            onDeleteComparison={this.onDeleteComparison}
            searchPlaceholder={searchContactPlaceholder}
            openingComparisonIds={openingComparisonIds}
          />
        );
      }
      case COMPARISONS_FILTER.DRAFTS: {
        return (
          <ComparisonsSection
            user={user}
            key={COMPARISONS_FILTER.DRAFTS}
            title={formatMessage('ComparisonHome.Filters.Drafts')}
            tableFields={[ComparisonsTableFields.Name, ComparisonsTableFields.LastModified, ComparisonsTableFields.Open, ComparisonsTableFields.Actions]}
            className={classes}
            comparisons={comparisons.drafts.list}
            hasMore={!!comparisons.drafts.nextToken}
            loadComparisons={this.loadComparisons}
            onOpenComparison={this.onOpenComparison}
            onSaveComparison={this.onSaveComparison}
            onDeleteComparison={this.onDeleteComparison}
            searchPlaceholder={formatMessage('ComparisonHome.SearchPlaceholder')}
            openingComparisonIds={openingComparisonIds}
          />
        );
      }
      case COMPARISONS_FILTER.COMPLIANCE: {
        return (
          <ComparisonsSection
            user={user}
            key={COMPARISONS_FILTER.COMPLIANCE}
            title={formatMessage('ComparisonHome.Filters.Compliance')}
            className={classes}
            tableFields={[ComparisonsTableFields.Name, ComparisonsTableFields.SavedTo, ComparisonsTableFields.DateCreated, ComparisonsTableFields.Open]}
            comparisons={comparisons.compliance.list}
            hasMore={!!comparisons.compliance.nextToken}
            loadComparisons={this.loadComparisons}
            onOpenComparison={this.onOpenComparison}
            onSaveComparison={this.onSaveComparison}
            onDeleteComparison={this.onDeleteComparison}
            searchPlaceholder={formatMessage('ComparisonHome.SearchPlaceholder')}
            openingComparisonIds={openingComparisonIds}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  showDeleteComparisonsModal = () => {
    this.setState({ showDeleteComparisonsModal: true });
  };

  hideDeleteComparisonsModal = () => {
    this.setState({
      selectedComparisonsForDelete: [],
      showDeleteComparisonsModal: false,
    });
  };

  onConfirmDeleteComparison = () => {
    const { selectedComparisonsForDelete } = this.state;

    return Promise.all(selectedComparisonsForDelete.map(c => this.props.deleteComparison(c))).then(() => {
      this.hideDeleteComparisonsModal();
    });
  };

  createBMOGIFComparison = () => {
    this.props.resetGif();
    this.props.skipEmptyState();
    this.props.createComparison();
    this.props.history.push(`${NAV_PATHS.COMPARISON}${COMPARISON_NAV_PATHS.GIF}`);
  };

  showStartComparison = () => {
    const { user } = this.props;
    return !!user && userHasPermission(user, PERMISSIONS.COMPARISONS_CREATE);
  };

  showStartBMOComparison = () => {
    const { user } = this.props;

    return !!user && userHasPermission(user, PERMISSIONS.BMO_GIF);
  };

  removeMissingItemsAndLoadComparison = async () => {
    const originalComparison = this.props.loadedComparison;
    if (!originalComparison) {
      return;
    }

    let newComparison = await this.props.removeMissingComparisonData(originalComparison);
    newComparison = await this.props.rerunComparison(newComparison);

    this.props.closeComparison(originalComparison);
    await this.props.loadComparison(newComparison);

    if (newComparison.contact && newComparison.contact._id) {
      this.props.getContactById(newComparison.contact._id).then(contact => {
        this.props.loadContactData(contact);
      });
    }

    this.setState({ showComparisonMissingItemsModal: false });
    this.props.history.push(NAV_PATHS.COMPARISON);
  };

  getComparisonsLayout = () => {
    const { user, isDeletingComparison, loadedComparison, comparisons, openingComparisonIds, ...rest } = this.props;
    const { selectedFilter, isLoadingRecentComparisons } = this.state;
    const { mode } = user.appConfig;
    const { theme } = user.preferences ?? { theme: 'default' };

    // This is part of the Correlate light theme update.
    // Note that the current "dark" mode is meant to be removed completely.
    // This would become the new comparisons page for all users.
    if (theme === ThemeMode.LIGHT && mode !== AppMode.FACT_SET) {
      return (
        <>
          <Header />
          <div className="comparison-home">
            <div className="container comparisons-list">
              <div className="row">
                <div className="col-12">
                  <ComparisonsList
                    comparisons={comparisons}
                    comparisonsFilter={selectedFilter}
                    filterComponent={
                      <ComparisonsFilterNavigation
                        user={user}
                        onChange={this.onFilterChange}
                        selected={selectedFilter}
                        contactLayout={getContactLayout(user)}
                      />
                    }
                    loadComparisons={this.loadComparisons}
                    onCreateComparison={this.onCreateComparison}
                    onCreateBMOComparison={this.createBMOGIFComparison}
                    onDeleteComparison={this.onDeleteComparison}
                    onOpenComparison={this.onOpenComparison}
                    onSaveComparison={this.onSaveComparison}
                    openingComparisonIds={openingComparisonIds}
                    options={{
                      showCreateComparison: this.showStartComparison(),
                      showStartBMOComparison: this.showStartBMOComparison(),
                    }}
                    user={user}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      );
    }

    switch (mode) {
      // This is the landing page for the Fact-set demo pages.
      case AppMode.FACT_SET:
        return (
          <FactSetLanding
            user={user}
            comparisons={comparisons}
            comparisonsFilter={selectedFilter}
            openingComparisonIds={openingComparisonIds}
            loadComparisons={this.loadComparisons}
            onOpenComparison={this.onOpenComparison}
            onSaveComparison={this.onSaveComparison}
            onDeleteComparison={this.onDeleteComparison}
            isLoadingRecentComparisons={isLoadingRecentComparisons}
            {...rest}
          />
        );
      // This is the current "dark" mode comparisons page that will be removed in the future
      default:
        return (
          <>
            <Header />
            <OverlappingBackground className="comparison-background" small />

            <main id="content" className="comparison-home">
              <div className="comparison-home-header">
                <h1 className="h3">
                  <FormattedMessage id="ComparisonHome.PageTitle" />
                </h1>

                <div className="actions-container">
                  {this.showStartComparison() && (
                    <PrimaryButton onClick={this.onCreateComparison}>
                      <i className="icon-plus" />
                      <FormattedMessage id="Comparison.StartComparison" />
                    </PrimaryButton>
                  )}
                  {this.showStartBMOComparison() && this.renderGifButton()}
                </div>
              </div>

              <div className="comparison-home-content d-flex">
                <ComparisonsFilterNavigation user={user} onChange={this.onFilterChange} selected={selectedFilter} contactLayout={getContactLayout(user)} />

                {this.getComparisonsList()}
              </div>
            </main>
          </>
        );
    }
  };

  render() {
    const { isDeletingComparison, loadedComparison } = this.props;
    const { selectedComparisonsForDelete, showDeleteComparisonsModal, showComparisonMissingItemsModal } = this.state;

    return (
      <>
        {this.getComparisonsLayout()}
        <DeleteComparisonsModal
          isDeleting={isDeletingComparison}
          comparisons={selectedComparisonsForDelete}
          show={showDeleteComparisonsModal}
          onCancel={this.hideDeleteComparisonsModal}
          onConfirm={this.onConfirmDeleteComparison}
        />
        {showComparisonMissingItemsModal && loadedComparison && (
          <ComparisonMissingItemsModal
            show={showComparisonMissingItemsModal}
            onCancel={() => {
              this.setState({ showComparisonMissingItemsModal: false });
              this.props.closeComparison(loadedComparison);
            }}
            onConfirm={this.removeMissingItemsAndLoadComparison}
            comparison={loadedComparison}
          />
        )}
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => {
  const loadedComparison = state.comparison.activeComparison;
  const openingComparisonParams = selectIsLoadingParamsEvery(state, ['OPEN_COMPARISON']);
  const openingComparisonIds = openingComparisonParams.reduce((acc, param: Dictionary<unknown>) => {
    if (param.comparisonId) {
      return [...acc, param.comparisonId as string];
    }

    return acc;
  }, [] as string[]);

  return {
    comparisons: state.comparison.comparisons,
    locale: state.intl.locale,
    loadedComparison,
    isDeletingComparison: selectIsLoadingEvery(state, [DELETE_COMPARISON.type]),
    openingComparisonIds,
    isPostMessageActive: state.postMessage.isPostMessageActive,
  };
};

export default withRouter(
  connect(mapStateToProps, {
    getContactById,
    loadContactData,
    getAssignedComparisons,
    getDraftComparisons,
    getComplianceComparisons,
    getRecentComparisons,
    loadComparison,
    deleteComparison,
    getComparisonById,
    clearComparisonsCache,
    skipEmptyState,
    resetGif,
    updateComparison,
    startLoaderAction,
    stopLoaderAction,
    removeMissingComparisonData,
    rerunComparison,
    createComparison,
    closeComparison,
    sendPostMessageGetContactsAndPortfolios,
  })(Comparisons),
);
